import { dayjs } from 'services/dayjs';
import { User } from 'services/interface';
import { isInOrganization } from 'services/accessControl';

const hasExpired = (date?: string): boolean => Boolean(date) && dayjs(date).isBefore(dayjs());

/**
 * An org member is expired by the organization's date alone (MDCI-984) — `paid_user` is not
 * consulted, so the banner still shows if the backend drops the flag once the org lapses.
 * A standalone user is only expired while `paid_user === 1`.
 */
export const getExpiredLicenseStatus = (user: User | null | undefined): boolean => {
    if (isInOrganization(user)) {
        return hasExpired(user?.organization?.expiration_date);
    }

    return user?.paid_user === 1 && hasExpired(user?.expiration_date);
};

/**
 * MDCI-984: the member consumes limits from an organization whose scan limits are all zero.
 * Only limits the org defines count — an org without limit fields falls back to the apikey
 * limits (see the PageHeader limit rows), so it is not a zero-limit org.
 */
export const isZeroLimitOrganization = (user: User | null | undefined): boolean => {
    if (!isInOrganization(user)) {
        return false;
    }

    const { limit_prevention, limit_reputation, limit_sandbox } = user?.organization ?? {};
    const limits = [limit_prevention, limit_reputation, limit_sandbox].filter((limit) => typeof limit === 'number');

    return limits.length > 0 && limits.every((limit) => limit === 0);
};

export type LicenseIssue = 'expired' | 'zero-limit';

/** Expiry wins over a zero-limit org: a lapsed org is the reason scans stop, not its limits. */
export const getLicenseIssue = (user: User | null | undefined): LicenseIssue | null => {
    if (getExpiredLicenseStatus(user)) {
        return 'expired';
    }

    return isZeroLimitOrganization(user) ? 'zero-limit' : null;
};

/**
 * MDCL-17831: the org-scoped workflow list (`/api/workflow/organization`) is only usable while the
 * organization's license is active. Once it lapses, a member still holding a valid personal
 * (standard) license owns apikey-scoped workflows, so route them to `/api/workflow` rather than
 * leaving them stuck on a dead org endpoint.
 */
export const shouldUseOrgWorkflowEndpoint = (user: User | null | undefined): boolean =>
    isInOrganization(user) && !hasExpired(user?.organization?.expiration_date);
